import { dictionaryApi } from './dictionaryClient';
import {
  normalizePadachhedBarnaSamuhaLink,
  type PadachhedBarnaSamuhaLink,
} from './ashtadhyayiPadachhedBarnaSamuhaApi';
import {
  normalizePadachhedGanaLink,
  type PadachhedGanaLink,
} from './ashtadhyayiPadachhedGanaApi';
import {
  normalizePadachhedPratyaharLink,
  type PadachhedPratyaharLink,
} from './ashtadhyayiPadachhedPratyaharApi';
import { normalizeAshtadhyayiBartik, type AshtadhyayiBartik } from './ashtadhyayiBartikApi';
import {
  normalizeSutraCommentaryText,
  type SutraCommentaryText,
} from './sutraCommentaryTextApi';

export type { PadachhedBarnaSamuhaLink, PadachhedGanaLink, PadachhedPratyaharLink };
export type { AshtadhyayiBartik };
export type { SutraCommentaryText };

export type Padachhed = {
  id: number;
  ashtadhyayiSutraId: number;
  order: number;
  text: string;
  bibhakti: number;
  bachan: number;
  digits: string | null;
  pratyaharLinks: PadachhedPratyaharLink[];
  barnaSamuhaLinks: PadachhedBarnaSamuhaLink[];
  ganaLinks: PadachhedGanaLink[];
};

/** Linked sūtra embedded on anubṛtti / adhikāra rows from GET /ashtadhyayi-sutra/:id. */
export type AnubrittiLinkSutra = Pick<
  AshtadhyayiSutra,
  | 'id'
  | 'itemKey'
  | 'a'
  | 'p'
  | 'n'
  | 'sutraText'
  | 'transliteration'
  | 'skChapter'
  | 'lskChapter'
  | 'type'
  | 'ss'
>;

/** Alias for clarity: same shape, used on adhikāra rows. */
export type AdhikarLinkSutra = AnubrittiLinkSutra;

/** Padachhed row embedded on anubṛtti from GET /ashtadhyayi-sutra/:id. */
export type AnubrittiPadachhedRef = {
  id: number;
  ashtadhyayiSutraId: number;
  order: number;
  text: string;
  bibhakti: number;
  bachan: number;
  digits: string | null;
};

export type Anubritti = {
  id: number;
  ashtadhyayiSutraId: number;
  order: number;
  text: string;
  /** Referenced sūtra id (matches linkSutra.id when expanded). */
  link: number | null;
  linkSutra: AnubrittiLinkSutra | null;
  ashtadhyayiPadachhedId: number | null;
  ashtadhyayiPadachhed: AnubrittiPadachhedRef | null;
};

export type Adhikar = {
  id: number;
  ashtadhyayiSutraId: number;
  order: number;
  text: string;
  /** Referenced sūtra id (matches linkSutra.id when expanded). */
  link: number | null;
  linkSutra: AdhikarLinkSutra | null;
};

export type AshtadhyayiSutra = {
  id: number;
  itemKey: string;
  a: string;
  p: string;
  n: string;
  sutraText: string;
  transliteration: string;
  skn: string;
  lskn: string;
  mskn: string;
  sskn: string;
  plskn: string;
  lpn: string;
  padachhed: string;
  padachheds: Padachhed[];
  bartiks: AshtadhyayiBartik[];
  anubrittis: Anubritti[];
  adhikars: Adhikar[];
  skChapter: string;
  lskChapter: string;
  type: string;
  an: string;
  ad: string;
  ss: string;
  rpn: string;
  psn: string;
  psChapter: string;
  sutraCommentaryTexts: SutraCommentaryText[];
};

export type AshtadhyayiPaginatedResponse = {
  data: AshtadhyayiSutra[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
};

/** POST body: all scalar fields except DB id and nested padachhed / anubṛtti / adhikāra rows (empty strings allowed). */
export type CreateAshtadhyayiBody = Omit<
  AshtadhyayiSutra,
  'id' | 'padachheds' | 'bartiks' | 'anubrittis' | 'adhikars' | 'sutraCommentaryTexts'
>;

export type UpdateAshtadhyayiBody = Partial<CreateAshtadhyayiBody>;

/** Bulk import body (source JSON field names). */
export type AshtadhyayiImportRow = {
  i: string;
  a: string;
  p: string;
  n: string;
  s: string;
  e: string;
  skn: string;
  lskn: string;
  mskn: string;
  sskn: string;
  plskn: string;
  lpn: string;
  pc: string;
  sk_chapter: string;
  lsk_chapter: string;
  type: string;
  an: string;
  ad: string;
  ss: string;
  rpn: string;
  psn: string;
  ps_chapter: string;
};

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (!isRecord(raw)) return raw;
  if ('data' in raw) {
    const inner = raw.data;
    if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
      return inner;
    }
  }
  return raw;
}

function str(o: Record<string, unknown>, key: string): string {
  if (!(key in o) || o[key] == null) return '';
  return String(o[key]);
}

function num(o: Record<string, unknown>, key: string): number | null {
  const v = o[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

export function normalizePadachhed(raw: unknown): Padachhed | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiSutraId = num(raw, 'ashtadhyayiSutraId') ?? num(raw, 'ashtadhyayi_sutra_id');
  const order = num(raw, 'order');
  const bibhakti = num(raw, 'bibhakti');
  const bachan = num(raw, 'bachan');
  if (id == null || ashtadhyayiSutraId == null || order == null || bibhakti == null || bachan == null) {
    return null;
  }
  const digitsRaw = raw.digits;
  const pratyaharLinksRaw = raw.pratyaharLinks ?? raw.pratyahar_links;
  const pratyaharLinks = Array.isArray(pratyaharLinksRaw)
    ? pratyaharLinksRaw.map(normalizePadachhedPratyaharLink).filter((x): x is PadachhedPratyaharLink => x != null)
    : [];
  const barnaSamuhaLinksRaw = raw.barnaSamuhaLinks ?? raw.barna_samuha_links;
  const barnaSamuhaLinks = Array.isArray(barnaSamuhaLinksRaw)
    ? barnaSamuhaLinksRaw
        .map(normalizePadachhedBarnaSamuhaLink)
        .filter((x): x is PadachhedBarnaSamuhaLink => x != null)
    : [];
  const ganaLinksRaw = raw.ganaLinks ?? raw.gana_links;
  const ganaLinks = Array.isArray(ganaLinksRaw)
    ? ganaLinksRaw.map(normalizePadachhedGanaLink).filter((x): x is PadachhedGanaLink => x != null)
    : [];
  return {
    id,
    ashtadhyayiSutraId,
    order,
    text: str(raw, 'text'),
    bibhakti,
    bachan,
    digits: digitsRaw == null ? null : String(digitsRaw),
    pratyaharLinks,
    barnaSamuhaLinks,
    ganaLinks,
  };
}

export function normalizeAnubrittiLinkSutra(raw: unknown): AnubrittiLinkSutra | null {
  const full = normalizeAshtadhyayiSutra(raw);
  if (!full) return null;
  return {
    id: full.id,
    itemKey: full.itemKey,
    a: full.a,
    p: full.p,
    n: full.n,
    sutraText: full.sutraText,
    transliteration: full.transliteration,
    skChapter: full.skChapter,
    lskChapter: full.lskChapter,
    type: full.type,
    ss: full.ss,
  };
}

export function normalizeAnubrittiPadachhedRef(raw: unknown): AnubrittiPadachhedRef | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiSutraId = num(raw, 'ashtadhyayiSutraId') ?? num(raw, 'ashtadhyayi_sutra_id');
  const order = num(raw, 'order');
  const bibhakti = num(raw, 'bibhakti');
  const bachan = num(raw, 'bachan');
  if (id == null || ashtadhyayiSutraId == null || order == null || bibhakti == null || bachan == null) {
    return null;
  }
  const digitsRaw = raw.digits;
  return {
    id,
    ashtadhyayiSutraId,
    order,
    text: str(raw, 'text'),
    bibhakti,
    bachan,
    digits: digitsRaw == null ? null : String(digitsRaw),
  };
}

export function normalizeAnubritti(raw: unknown): Anubritti | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiSutraId = num(raw, 'ashtadhyayiSutraId') ?? num(raw, 'ashtadhyayi_sutra_id');
  const order = num(raw, 'order');
  if (id == null || ashtadhyayiSutraId == null || order == null) return null;
  const linkSutraRaw = raw.linkSutra ?? raw.link_sutra;
  const linkSutra = linkSutraRaw ? normalizeAnubrittiLinkSutra(linkSutraRaw) : null;
  const link = num(raw, 'link') ?? linkSutra?.id ?? null;
  const padachhedRaw = raw.ashtadhyayiPadachhed ?? raw.ashtadhyayi_padachhed;
  const ashtadhyayiPadachhed = padachhedRaw ? normalizeAnubrittiPadachhedRef(padachhedRaw) : null;
  const ashtadhyayiPadachhedId =
    num(raw, 'ashtadhyayiPadachhedId') ??
    num(raw, 'ashtadhyayi_padachhed_id') ??
    ashtadhyayiPadachhed?.id ??
    null;
  return {
    id,
    ashtadhyayiSutraId,
    order,
    text: str(raw, 'text'),
    link,
    linkSutra,
    ashtadhyayiPadachhedId,
    ashtadhyayiPadachhed,
  };
}

export function normalizeAdhikar(raw: unknown): Adhikar | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiSutraId = num(raw, 'ashtadhyayiSutraId') ?? num(raw, 'ashtadhyayi_sutra_id');
  const order = num(raw, 'order');
  if (id == null || ashtadhyayiSutraId == null || order == null) return null;
  const linkSutraRaw = raw.linkSutra ?? raw.link_sutra;
  const linkSutra = linkSutraRaw ? normalizeAnubrittiLinkSutra(linkSutraRaw) : null;
  const link = num(raw, 'link') ?? linkSutra?.id ?? null;
  return {
    id,
    ashtadhyayiSutraId,
    order,
    text: str(raw, 'text'),
    link,
    linkSutra,
  };
}

export function normalizeAshtadhyayiSutra(raw: unknown): AshtadhyayiSutra | null {
  if (!isRecord(raw)) return null;
  const idRaw = raw.id;
  const id = typeof idRaw === 'number' ? idRaw : Number(idRaw);
  if (!Number.isFinite(id)) return null;
  const padachhedsRaw = raw.padachheds ?? raw.padaccheds;
  const padachheds = Array.isArray(padachhedsRaw)
    ? padachhedsRaw
        .map(normalizePadachhed)
        .filter((x): x is Padachhed => x != null)
        .sort((a, b) => a.order - b.order)
    : [];
  const anubrittisRaw = raw.anubrittis ?? raw.anubritti;
  const anubrittis = Array.isArray(anubrittisRaw)
    ? anubrittisRaw
        .map(normalizeAnubritti)
        .filter((x): x is Anubritti => x != null)
        .sort((a, b) => a.order - b.order)
    : [];
  const adhikarsRaw = raw.adhikars ?? raw.adhikar ?? raw.adhikāras;
  const adhikars = Array.isArray(adhikarsRaw)
    ? adhikarsRaw
        .map(normalizeAdhikar)
        .filter((x): x is Adhikar => x != null)
        .sort((a, b) => a.order - b.order)
    : [];
  const bartiksRaw = raw.bartiks ?? raw.bartik ?? raw.vartikas;
  const bartiks = Array.isArray(bartiksRaw)
    ? bartiksRaw
        .map(normalizeAshtadhyayiBartik)
        .filter((x): x is AshtadhyayiBartik => x != null)
        .sort((a, b) => a.order - b.order)
    : [];
  const sutraCommentaryTextsRaw =
    raw.sutraCommentaryTexts ?? raw.sutra_commentary_texts ?? raw.commentaryTexts;
  const sutraCommentaryTexts = Array.isArray(sutraCommentaryTextsRaw)
    ? sutraCommentaryTextsRaw
        .map(normalizeSutraCommentaryText)
        .filter((x): x is SutraCommentaryText => x != null)
    : [];
  return {
    id,
    itemKey: str(raw, 'itemKey') || str(raw, 'i'),
    a: str(raw, 'a'),
    p: str(raw, 'p'),
    n: str(raw, 'n'),
    sutraText: str(raw, 'sutraText'),
    transliteration: str(raw, 'transliteration'),
    skn: str(raw, 'skn'),
    lskn: str(raw, 'lskn'),
    mskn: str(raw, 'mskn'),
    sskn: str(raw, 'sskn'),
    plskn: str(raw, 'plskn'),
    lpn: str(raw, 'lpn'),
    padachhed: str(raw, 'padachhed') || str(raw, 'padacched'),
    padachheds,
    bartiks,
    anubrittis,
    adhikars,
    skChapter: str(raw, 'skChapter') || str(raw, 'sk_chapter'),
    lskChapter: str(raw, 'lskChapter') || str(raw, 'lsk_chapter'),
    type: str(raw, 'type'),
    an: str(raw, 'an'),
    ad: str(raw, 'ad'),
    ss: str(raw, 'ss'),
    rpn: str(raw, 'rpn'),
    psn: str(raw, 'psn'),
    psChapter: str(raw, 'psChapter') || str(raw, 'ps_chapter'),
    sutraCommentaryTexts,
  };
}

/** Normalize GET /ashtadhyayi-sutra/:id (entity or `{ data }` wrapper). */
export function normalizeAshtadhyayiDetailResponse(raw: unknown): AshtadhyayiSutra | null {
  return normalizeAshtadhyayiSutra(unwrapEntityPayload(raw));
}

export async function listAshtadhyayiPaginated(
  page: number = 1,
  limit: number = 50
): Promise<AshtadhyayiPaginatedResponse> {
  const safePage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
  const safeLimit = Number.isFinite(limit) && limit > 0 ? Math.min(200, Math.floor(limit)) : 50;
  const res = await dictionaryApi.get<unknown>('/ashtadhyayi-sutra/paginated', {
    params: { page: safePage, limit: safeLimit },
  });
  const payload = res.data as Partial<AshtadhyayiPaginatedResponse> | null;
  if (!payload || !Array.isArray(payload.data) || !isRecord(payload.pagination)) {
    throw new Error('Invalid paginated ashtadhyayi response');
  }
  const data = payload.data
    .map((row) => normalizeAshtadhyayiSutra(row))
    .filter((x): x is AshtadhyayiSutra => x != null);
  const pg = payload.pagination;
  return {
    data,
    pagination: {
      page: typeof pg.page === 'number' ? pg.page : Number(pg.page) || safePage,
      limit: typeof pg.limit === 'number' ? pg.limit : Number(pg.limit) || safeLimit,
      total: typeof pg.total === 'number' ? pg.total : Number(pg.total) || 0,
      totalPages: typeof pg.totalPages === 'number' ? pg.totalPages : Number(pg.totalPages) || 0,
    },
  };
}

export async function getAshtadhyayi(id: number): Promise<AshtadhyayiSutra> {
  const res = await dictionaryApi.get<unknown>(`/ashtadhyayi-sutra/${id}`);
  const row = normalizeAshtadhyayiDetailResponse(res.data);
  if (!row) {
    throw new Error('Invalid ashtadhyayi response');
  }
  return row;
}

export async function createAshtadhyayi(body: CreateAshtadhyayiBody): Promise<AshtadhyayiSutra> {
  const res = await dictionaryApi.post<unknown>('/ashtadhyayi-sutra', body);
  const row = normalizeAshtadhyayiSutra(unwrapEntityPayload(res.data));
  if (!row) {
    throw new Error('Invalid ashtadhyayi response');
  }
  return row;
}

export async function updateAshtadhyayi(id: number, body: UpdateAshtadhyayiBody): Promise<AshtadhyayiSutra> {
  const res = await dictionaryApi.patch<unknown>(`/ashtadhyayi-sutra/${id}`, body);
  const row = normalizeAshtadhyayiSutra(unwrapEntityPayload(res.data));
  if (!row) {
    throw new Error('Invalid ashtadhyayi response');
  }
  return row;
}

export async function deleteAshtadhyayi(id: number): Promise<void> {
  await dictionaryApi.delete(`/ashtadhyayi-sutra/${id}`);
}

export async function importAshtadhyayi(data: AshtadhyayiImportRow[]): Promise<void> {
  await dictionaryApi.post('/ashtadhyayi-sutra/import', { data });
}

export function emptyCreateAshtadhyayiBody(): CreateAshtadhyayiBody {
  return {
    itemKey: '',
    a: '',
    p: '',
    n: '',
    sutraText: '',
    transliteration: '',
    skn: '',
    lskn: '',
    mskn: '',
    sskn: '',
    plskn: '',
    lpn: '',
    padachhed: '',
    skChapter: '',
    lskChapter: '',
    type: '',
    an: '',
    ad: '',
    ss: '',
    rpn: '',
    psn: '',
    psChapter: '',
  };
}

export function sutraToFormValues(s: AshtadhyayiSutra | CreateAshtadhyayiBody): CreateAshtadhyayiBody {
  return {
    itemKey: s.itemKey,
    a: s.a,
    p: s.p,
    n: s.n,
    sutraText: s.sutraText,
    transliteration: s.transliteration,
    skn: s.skn,
    lskn: s.lskn,
    mskn: s.mskn,
    sskn: s.sskn,
    plskn: s.plskn,
    lpn: s.lpn,
    padachhed: s.padachhed,
    skChapter: s.skChapter,
    lskChapter: s.lskChapter,
    type: s.type,
    an: s.an,
    ad: s.ad,
    ss: s.ss,
    rpn: s.rpn,
    psn: s.psn,
    psChapter: s.psChapter,
  };
}
